import { h, Component } from 'preact';
import { observer } from 'mobx-preact';
import util from 'preact-util';
import { IntlProvider, Text, Localizer, withText } from 'preact-i18n';

import { formatDate } from '../../lib/clubOps';
import {
    buildCountEntry, buildCountSeries, countDiff, lastCountEntry, purchaseTotal,
} from '../../lib/stockCount';
import LineChartCompare from '../gui/lineChartCompare';


const toNumber = (val) => {
    if (val == null) return 0;
    if (typeof val === 'object' && val.$numberDecimal != null) return Number(val.$numberDecimal);
    if (typeof val === 'string') return Number(val.replace(',', '.')) || 0;
    return Number(val) || 0;
};

const formatCurrency = (n) => new Intl.NumberFormat('nb-NO', {
    style: 'currency',
    currency: 'NOK',
    maximumFractionDigits: 2
}).format(toNumber(n));

const formatNumber = (n) => new Intl.NumberFormat('nb-NO', {
    maximumFractionDigits: 2
}).format(toNumber(n));

const formatDiff = (n) => `${toNumber(n) > 0 ? '+' : ''}${formatNumber(n)}`;

const diffClass = (n) => {
    const diff = toNumber(n);
    if (diff < 0) return 'text-danger';
    if (diff > 0) return 'text-warning';
    return 'text-success';
};


@withText(props => ({
    qtyTitle: <Text id='product.qty-placeholder'>Input incoming qty</Text>,
    inPriceTitle: <Text id='product.inprice-placeholder'>Purchase price per unit</Text>,
    countedTitle: <Text id='product.counted-placeholder'>Input counted qty</Text>,
    expectedLabel: <Text id='product.expected-stock'>Expected stock</Text>,
    countedLabel: <Text id='product.counted'>Counted</Text>,
}))
@observer
class ProductStockAdjustment extends Component {
    constructor(props) {
        super(props);
        this.inputRef = null;
        this.countedRef = null;
        this.state = {
            // The Ammo-innkjøp table opens the drawer straight in count mode.
            mode: props.mode === 'count' ? 'count' : 'purchase',
            movements: null,
            movementsLoading: false,
        };
    }

    /**
     * Bought / sold since the last count for the reconciliation box in count mode.
     * Officer-only endpoint; a 403 for non-officers just leaves the box out.
     */
    loadMovements = async () => {
        const { id } = this.props;
        if (!id) return;
        this.setState({ movementsLoading: true });
        const response = await util.fetchApi('/api/ammosales/stock-movements', { method: 'GET' }, { productId: id });
        const movements = response?.status === 200 && response?.data ? response.data : null;
        this.setState({ movements, movementsLoading: false });
    }

    loadAll = async () => {
        const { id } = this.props;
        const { productStore } = this.props.stores;
        console.log('stockAdjustment.loadAll', id);
        productStore.updateKeyValue('product', {});
        if (id) {
            const query = {};
            await productStore.load(id, false, { query, addData: ['tags'], offset: 0, limit: 1 });
        }
    }

    setMode = (mode) => {
        this.setState({
            mode, qty: '', inPrice: '', counted: '',
        }, () => {
            const ref = mode === 'count' ? this.countedRef : this.inputRef;
            if (ref) {
                ref.focus();
            }
        });
        if (mode === 'count' && !this.state.movements) {
            this.loadMovements();
        }
    }

    qtyInput = (e) => {
        const qty = e.target.value;
        this.setState({ qty });
    }

    inPriceInput = (e) => {
        const inPrice = e.target.value;
        this.setState({ inPrice });
    }

    countedInput = (e) => {
        const counted = e.target.value;
        this.setState({ counted });
    }

    handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            this.addQtyToProduct();
            // this.doSearch(this.state.search);
        } else if (e.key === 'Escape') {
            e.preventDefault();
            this.setState({ qty: '', inPrice: '' });
            // this.clearSearch();
        }
    }

    handleCountKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            this.saveCount();
        } else if (e.key === 'Escape') {
            e.preventDefault();
            this.setState({ counted: '' });
        }
    }

    addQtyToProduct = async () => {
        const { qty, inPrice } = this.state;
        if (!qty) return;
        const { appState, productStore } = this.props.stores;
        const { product } = productStore;

        const currentStock = product?.inStock || 0;
        const newStock = currentStock + parseFloat(qty);
        const parsedQty = parseFloat(qty);
        const parsedInPrice = parseFloat(inPrice);

        // Build update object
        const updateData = {
            inStock: newStock,
        };

        // Add inPrice if provided
        if (inPrice && !isNaN(parsedInPrice)) {
            updateData.inPrice = parsedInPrice;
        }

        // Add stock history entry
        updateData.stockHistory = {
            addedDate: new Date().toISOString(),
            qty: parsedQty,
            inPrice: !isNaN(parsedInPrice) ? parsedInPrice : (product?.inPrice || 0),
        };

        // Make API call to update product
        await util.fetchApi(`/api/products/${product?.id}`, { method: 'PATCH' }, updateData);

        // Keep the product list (productStore.products) in sync so the new stock
        // shows up immediately, without the user having to re-search. The PATCH
        // above only touches the server, and loadAll() below only refreshes the
        // singular productStore.product (the drawer) — neither updates the array
        // the table renders. updateField() mutates the matching row in place,
        // mirroring what saveField(..., true) used to do before this flow gained
        // inPrice/stockHistory support.
        productStore.updateField(product?.id, 'inStock', newStock);
        if (updateData.inPrice !== undefined) {
            productStore.updateField(product?.id, 'inPrice', updateData.inPrice);
        }

        // Reload product to get updated data
        await this.loadAll();

        this.setState({ qty: '', inPrice: '' });

        const { drawerLevel, callback = () => {} } = this.props;
        callback();
        appState.toggleDrawer(false, drawerLevel);
    }

    /**
     * Stock count (opptelling): the shelf is counted, inStock is set to the counted
     * number and a typed `count` entry with the diff is appended to stockHistory
     * (see lib/stockCount.js). Same PATCH path as a purchase, so the list refresh
     * and drawer close mirror addQtyToProduct.
     */
    saveCount = async () => {
        const { counted } = this.state;
        if (counted === '' || counted == null) return;
        const parsedCounted = parseFloat(counted);
        if (isNaN(parsedCounted) || parsedCounted < 0) return;
        const { appState, productStore, userStore } = this.props.stores;
        const { product } = productStore;

        const entry = buildCountEntry({
            counted: parsedCounted,
            expected: product?.inStock || 0,
            userId: userStore.user?.id,
        });

        await util.fetchApi(`/api/products/${product?.id}`, { method: 'PATCH' }, {
            inStock: entry.counted,
            stockHistory: entry,
        });

        productStore.updateField(product?.id, 'inStock', entry.counted);

        await this.loadAll();

        this.setState({ counted: '' });

        const { drawerLevel, callback = () => {} } = this.props;
        callback();
        appState.toggleDrawer(false, drawerLevel);
    }

    componentDidMount() {
        this.loadAll();
        if (this.state.mode === 'count') {
            this.loadMovements();
        }
        // The count input only exists once the product has loaded (showCount), so
        // focus is handled by setMode / the ref callback for count mode.
        if (this.inputRef) {
            this.inputRef.focus();
        }
    }

    componentWillUnmount() {
        this.setState({ qty: '', inPrice: '', counted: '' });
    }

    render() {
        const {
            qty, inPrice, counted, mode, movements, movementsLoading,
        } = this.state;
        const {
            id, qtyTitle, inPriceTitle, countedTitle, expectedLabel, countedLabel,
        } = this.props;
        const { appState, productStore, userStore } = this.props.stores;
        const { product } = productStore;

        const currentStock = product?.inStock || 0;
        const newStock = currentStock + parseFloat(qty || 0);
        // What the purchase costs — the same qty × per-unit price the club ledger
        // mirror stores. Shown live so a batch total typed into the per-unit field
        // gives an obviously wrong sum before the purchase is saved.
        const totalCost = purchaseTotal(qty, inPrice);

        const { user, isAdmin, customer } = userStore;
        const darkmode = util.getNestedValue(user, 'settings.darkmode');

        // Stock counts only make sense for ammo products in clubs where registrations
        // actually move inStock; stockless clubs keep their stock in another system.
        const ammoStockless = appState?.currentCustomer?.featureFlags?.ammoStockless
            || userStore?.customer?.featureFlags?.ammoStockless;
        const showCount = !!product?.isAmmo && parseInt(ammoStockless, 10) !== 1;
        const isCountMode = showCount && mode === 'count';
        const hasCounted = counted !== '' && counted != null;
        const diff = countDiff(counted, currentStock);
        const lastCount = lastCountEntry(product?.stockHistory);
        const countLog = movements?.counts || [];
        const bookDiff = toNumber(movements?.bookDiff);
        const countChart = countLog.length >= 2
            ? buildCountSeries(countLog, { formatDate, expectedLabel, countedLabel })
            : null;
        const isMobile = !!appState?.isMobile;

        return (
            <>
                <div class='w-100 d-flex justify-content-center position-relative'>
                    <div class='w-100' style='max-width: 960px; padding-top: 20px; padding-bottom: 100px;'>
                        <div class={`d-flex flex-column mx-3 px-4 py-3 box-container ${darkmode ? 'bg-darkmode' : 'bg-lightmode'}`}>
                            <div class='d-flex align-items-center justify-content-between '>
                                <h5 class='m-0'>
                                    {isCountMode ? <Text id='product.count-stock'>Stock count</Text> : <Text id='product.add-stock'>Product Add Stock</Text>}
                                </h5>
                                <span class='text-muted'>ID: {id}</span>
                            </div>
                            <span class='text-muted font-weight-light'> {product.name}</span>

                            {showCount && <div class='d-flex flex-row gap-2 mt-2 mb-3'>
                                <button
                                    type='button'
                                    class={`btn btn-sm rounded-pill ${isCountMode ? 'btn-outline-primary' : 'btn-primary'}`}
                                    onClick={() => this.setMode('purchase')}
                                >
                                    <i class='fa-regular fa-box-open me-1' />
                                    <Text id='product.purchase-tab'>Purchase</Text>
                                </button>
                                <button
                                    type='button'
                                    class={`btn btn-sm rounded-pill ${isCountMode ? 'btn-primary' : 'btn-outline-primary'}`}
                                    onClick={() => this.setMode('count')}
                                >
                                    <i class='fa-regular fa-clipboard-check me-1' />
                                    <Text id='product.count-tab'>Stock count</Text>
                                </button>
                            </div>}

                            {isCountMode ? <>
                                <div class='d-flex flex-row justify-content-between'>
                                    <div class='flex-fill input-group'>
                                        <div class='input-group-text rounded-pill-left'>
                                            <i class='fa-regular fa-clipboard-check' />
                                        </div>
                                        <input
                                            class={`form-control ${!hasCounted ? 'rounded-pill-right' : ''}`}
                                            type='text'
                                            inputmode='numeric'
                                            value={counted}
                                            placeholder={`${countedTitle}`}
                                            onInput={this.countedInput}
                                            onKeyDown={this.handleCountKeyDown}
                                            ref={(input) => { this.countedRef = input; }}
                                        />
                                        {hasCounted && <button class='btn btn-primary rounded-pill-right' type='button' onClick={this.saveCount}>
                                            <i class='fa-solid fa-save' />
                                        </button>}
                                    </div>
                                </div>
                                <small class='text-muted mt-1'>
                                    <Text id='product.count-help'>Count the shelf and enter the number. Stock is set to the counted number.</Text>
                                </small>

                                {movementsLoading && <small class='text-body-secondary mt-2'>
                                    <i class='fa-solid fa-spinner fa-spin me-1' />
                                    <Text id='product.count-loading'>Loading purchases and sales...</Text>
                                </small>}
                                {movements && <div class='mt-3 p-2 rounded-lg bg-body-secondary'>
                                    <div class='small text-body-secondary mb-1'>
                                        {movements.lastCount ? (
                                            <Text id='product.count-since' fields={{ date: formatDate(movements.lastCount.addedDate) }}>
                                                Since last count {formatDate(movements.lastCount.addedDate)}
                                            </Text>
                                        ) : (
                                            <Text id='product.count-no-previous'>No previous count. All purchases and sales are included.</Text>
                                        )}
                                    </div>
                                    <div class='d-flex flex-row flex-wrap gap-3'>
                                        {movements.lastCount && <span>
                                            <span class='text-body-secondary'><Text id='product.count-prev'>Last count</Text>:</span>
                                            {' '}<strong>{formatNumber(movements.lastCount.counted)}</strong>
                                        </span>}
                                        <span>
                                            <span class='text-body-secondary'><Text id='product.count-bought'>Bought</Text>:</span>
                                            {' '}<strong class='text-success'>+{formatNumber(movements.bought)}</strong>
                                        </span>
                                        <span>
                                            <span class='text-body-secondary'><Text id='product.count-sold'>Sold</Text>:</span>
                                            {' '}<strong class='text-danger'>−{formatNumber(movements.sold)}</strong>
                                        </span>
                                        <span>
                                            <span class='text-body-secondary'><Text id='product.expected-stock'>Expected stock</Text>:</span>
                                            {' '}<strong>{formatNumber(movements.expected)}</strong>
                                        </span>
                                    </div>
                                    {bookDiff !== 0 && <div class='small text-warning mt-1'>
                                        <i class='fa-regular fa-triangle-exclamation me-1' />
                                        <Text id='product.count-book-mismatch' fields={{ diff: formatDiff(bookDiff) }}>
                                            Booked stock differs from last count + bought − sold by {formatDiff(bookDiff)}.
                                        </Text>
                                    </div>}
                                </div>}

                                <div class='d-flex flex-row flex-wrap mb-3 mt-3'>
                                    <div class='d-flex flex-column me-3 mb-2 p-2 rounded-lg shadow-sm' style='min-width: 160px;'>
                                        <div class='text-muted small'><Text id='product.expected-stock'>Expected stock</Text></div>
                                        <div class='h5 m-0'>{formatNumber(currentStock || 0)}</div>
                                    </div>
                                    <div class='d-flex flex-column me-3 mb-2 p-2 rounded-lg shadow-sm' style='min-width: 160px;'>
                                        <div class='text-muted small'><Text id='product.counted'>Counted</Text></div>
                                        <div class='h5 m-0'>{hasCounted ? formatNumber(counted) : '–'}</div>
                                    </div>
                                    <div class='d-flex flex-column me-3 mb-2 p-2 rounded-lg shadow-sm' style='min-width: 160px;'>
                                        <div class='text-muted small'><Text id='product.count-diff'>Difference</Text></div>
                                        <div class={`h5 m-0 ${hasCounted ? diffClass(diff) : ''}`}>{hasCounted ? formatDiff(diff) : '–'}</div>
                                    </div>
                                    <div class='d-flex flex-column me-3 mb-2 p-2 rounded-lg shadow-sm' style='min-width: 160px;'>
                                        <div class='text-muted small'><Text id='product.last-count'>Last count</Text></div>
                                        <div class='h5 m-0'>
                                            {lastCount ? <>
                                                {formatDate(lastCount.addedDate)}
                                                <span class={`ms-2 ${diffClass(lastCount.diff)}`}>{formatDiff(lastCount.diff)}</span>
                                            </> : '–'}
                                        </div>
                                    </div>
                                </div>

                                {countChart && <div class='mb-3'>
                                    <div class='text-muted small mb-1'>
                                        <i class='fa-duotone fa-chart-line me-1' />
                                        <Text id='product.count-chart'>Count history</Text>
                                    </div>
                                    <LineChartCompare
                                        series={countChart.series}
                                        xLabels={countChart.xLabels}
                                        xTickLabels={countChart.xTickLabels}
                                        xTickEvery={countChart.xLabels.length > (isMobile ? 4 : 8) ? 2 : 1}
                                        width={isMobile ? 380 : 720}
                                        height={isMobile ? 260 : 220}
                                        paddingLeft={40}
                                        strokeWidth={3}
                                        fontSize={11}
                                    />
                                </div>}

                                {movements && <div class='mb-3'>
                                    <div class='text-muted small mb-1'><Text id='product.count-log'>Previous counts</Text></div>
                                    {countLog.length === 0 ? (
                                        <small class='text-body-secondary'><Text id='product.count-log-empty'>No counts registered yet.</Text></small>
                                    ) : (
                                        <div class='table-responsive'>
                                            <table class='table table-sm align-middle mb-0'>
                                                <thead>
                                                    <tr>
                                                        <th><Text id='product.count-date'>Date</Text></th>
                                                        <th class='text-end'><Text id='product.expected-stock'>Expected stock</Text></th>
                                                        <th class='text-end'><Text id='product.counted'>Counted</Text></th>
                                                        <th class='text-end'><Text id='product.count-diff'>Difference</Text></th>
                                                        <th><Text id='product.count-by'>Counted by</Text></th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {countLog.map(entry => (
                                                        <tr key={entry.addedDate}>
                                                            <td>{formatDate(entry.addedDate)}</td>
                                                            <td class='text-end'>{formatNumber(entry.expected)}</td>
                                                            <td class='text-end'>{formatNumber(entry.counted)}</td>
                                                            <td class={`text-end ${diffClass(entry.diff)}`}>{formatDiff(entry.diff)}</td>
                                                            <td>{entry.userName || '–'}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </div>}
                            </> : <>
                                <div class='d-flex flex-row justify-content-between mb-2'>
                                    <div class='flex-fill input-group'>
                                        <div class='input-group-text rounded-pill-left'>
                                            <i class='fa-regular fa-box-open'></i>
                                        </div>
                                        <input
                                            class='form-control rounded-pill-right'
                                            type='text'
                                            value={qty}
                                            placeholder={`${qtyTitle}`}
                                            onInput={this.qtyInput}
                                            onKeyDown={this.handleKeyDown}
                                            ref={(input) => { this.inputRef = input; }}
                                        />
                                    </div>
                                </div>
                                <div class='d-flex flex-row justify-content-between'>
                                    <div class='flex-fill input-group'>
                                        <div class='input-group-text rounded-pill-left'>
                                            <i class='fa-regular fa-money-bill'></i>
                                        </div>
                                        <input
                                            class={`form-control ${!qty ? 'rounded-pill-right' : ''}`}
                                            type='text'
                                            value={inPrice}
                                            placeholder={`${inPriceTitle}`}
                                            onInput={this.inPriceInput}
                                            onKeyDown={this.handleKeyDown}
                                        />
                                        {qty && <button class='btn btn-primary rounded-pill-right' type='button' onClick={this.addQtyToProduct}>
                                            <i class='fa-solid fa-save'></i>
                                        </button>}
                                    </div>
                                </div>
                                {totalCost > 0 && <div class='mt-2 p-2 rounded-lg bg-body-secondary'>
                                    <span class='text-body-secondary me-1'>
                                        <Text id='product.inprice-total'>Total purchase</Text>:
                                    </span>
                                    <span class='text-body'>
                                        {formatNumber(qty)} × {formatNumber(inPrice)} kr =
                                        {' '}<strong>{formatNumber(totalCost)} kr</strong>
                                    </span>
                                </div>}
                                <small class='text-muted mt-1'>
                                    <Text id='product.inprice-vat-help'>Purchase price per unit, excluding VAT.</Text>
                                </small>

                                {/* Metric cards */}
                                <div class='d-flex flex-row flex-wrap mb-3 mt-3'>
                                    <div class='d-flex flex-column me-3 mb-2 p-2 rounded-lg shadow-sm' style='min-width: 160px;'>
                                        <div class='text-muted small'><Text id='product.current-stock'>Current stock</Text></div>
                                        <div class='h5 m-0'>{formatNumber(currentStock || 0)}</div>
                                    </div>
                                    <div class='d-flex flex-column me-3 mb-2 p-2 rounded-lg shadow-sm' style='min-width: 160px;'>
                                        <div class='text-muted small'><Text id='product.new-stock'>New stock</Text></div>
                                        <div class='h5 m-0'>{formatNumber(newStock || 0)}</div>
                                    </div>
                                    <div class='d-flex flex-column me-3 mb-2 p-2 rounded-lg shadow-sm' style='min-width: 160px;'>
                                        <div class='text-muted small'><Text id='product.current-inprice'>Current purchase price</Text></div>
                                        <div class='h5 m-0'>{formatCurrency(product?.inPrice || 0)}</div>
                                    </div>
                                </div>
                            </>}

                        </div>
                    </div>
                </div>
            </>
        );
    }
}
export default ProductStockAdjustment;
